import { Instant, Now, PlainDateTime } from "@/temporal"
import type { InputHTMLAttributes } from "vue"
import { inferProps } from "vue-utils"

type Props = {
	value: Instant | null
	min?: Instant | null
	max?: Instant | null
} & (
	| {
			setValue(value: Instant): void
			required: true
	  }
	| {
			setValue(value: Instant | null): void
			required?: false
	  }
)

function InstantInput(
	props: Props & Omit<InputHTMLAttributes, keyof Props>,
	{ attrs }: { attrs: Omit<InputHTMLAttributes, keyof Props> }
) {
	function handleOnInput(event: Event) {
		const value = (event.target as HTMLInputElement).value
		if (value === "" && !props.required) {
			props.setValue(null)
		} else if (value !== "") {
			let date
			try {
				date = PlainDateTime.from(value).toZonedDateTime(Now.timeZoneId()).toInstant()
			} catch {
				return
			}
			props.setValue(date)
		}
	}

	function getPlainDate() {
		if (!props.value) {
			return null
		}
		return props.value
			.toZonedDateTimeISO(Now.timeZoneId())
			.toPlainDateTime()
			.round({
				smallestUnit: "minute",
				roundingMode: "halfCeil",
			})
			.toString({ smallestUnit: "minute" })
	}

	return (
		<input
			type="datetime-local"
			value={getPlainDate()}
			onChange={handleOnInput}
			required={props.required}
			min={props.min?.toJSON()}
			max={props.max?.toJSON()}
			{...attrs}
		/>
	)
}

InstantInput.props = inferProps<Props>()

export default InstantInput
